import type { DraftDocument, EditorialTask, Quiz, StudyTopic } from '@buakei/types';

/** DEMO FIXTURES — fictitious drafts, study content and editorial tasks. */

export const DEMO_DRAFTS: DraftDocument[] = [
  {
    id: 'drf_demo_memo',
    dataOrigin: 'demo',
    title: 'Research memo — demonstration formation question',
    kind: 'memo',
    reviewStatus: 'ai_draft',
    updatedAt: '2026-10-18',
    body: 'DEMO DRAFT\n\nIssue: whether a demonstration agreement was formed by an oral reply.\n\nShort answer: within the demonstration sources, likely not, where the offer required written acceptance.\n\n[Lawyer review required before any use.]',
  },
  {
    id: 'drf_demo_letter',
    dataOrigin: 'demo',
    title: 'Letter of advice (demonstration template)',
    kind: 'letter',
    reviewStatus: 'under_review',
    updatedAt: '2026-10-16',
    body: 'DEMO DRAFT\n\nDear [Fictitious Client],\n\nThis demonstration letter shows how drafts move through review. It contains no legal advice.',
  },
  {
    id: 'drf_demo_clause',
    dataOrigin: 'demo',
    title: 'Clause — demonstration governing terms',
    kind: 'clause',
    reviewStatus: 'approved_internal',
    updatedAt: '2026-10-10',
    body: 'DEMO CLAUSE\n\nThis demonstration agreement is governed by the fictitious law of the Demonstration jurisdiction.',
  },
];

export const DEMO_TOPICS: StudyTopic[] = [
  {
    id: 'top_formation',
    dataOrigin: 'demo',
    subject: 'Contract (demo)',
    title: 'Formation of agreements',
    summary:
      'Offer, acceptance and the role of silence, using the fictitious Demonstration Contracts Act.',
    progress: 60,
  },
  {
    id: 'top_consideration',
    dataOrigin: 'demo',
    subject: 'Contract (demo)',
    title: 'Consideration',
    summary: 'Why each side must give something of value in exchange.',
    progress: 20,
  },
  {
    id: 'top_reading_judgments',
    dataOrigin: 'demo',
    subject: 'Legal method (demo)',
    title: 'Reading a judgment',
    summary: 'Finding the issue, the holding and the reasoning in a fictitious judgment.',
    progress: 0,
  },
];

export const DEMO_QUIZ: Quiz = {
  id: 'quiz_demo_formation',
  dataOrigin: 'demo',
  topicId: 'top_formation',
  title: 'Formation of agreements — practice quiz',
  questions: [
    {
      id: 'q1',
      prompt: 'Under the fictitious Demonstration Contracts Act, when is an agreement formed?',
      options: [
        { id: 'a', text: 'When the offer is made' },
        { id: 'b', text: 'When the offer is accepted in the manner it specifies' },
        { id: 'c', text: 'When both parties sign any document' },
      ],
      correctOptionId: 'b',
      explanation:
        'Section 2(1) of the fictitious Act ties formation to acceptance in the specified manner.',
      citationIds: ['psg_demo_ca_s2'],
    },
    {
      id: 'q2',
      prompt: 'Does silence amount to acceptance under the fictitious Act?',
      options: [
        { id: 'a', text: 'Yes, always' },
        { id: 'b', text: 'Only between businesses' },
        { id: 'c', text: 'No' },
      ],
      correctOptionId: 'c',
      explanation:
        'Section 2(2) of the fictitious Act states that silence does not amount to acceptance.',
      citationIds: ['psg_demo_ca_s2'],
    },
    {
      id: 'q3',
      prompt: 'In Alpha Example v Beta Illustration (DEMO), why was no agreement formed?',
      options: [
        { id: 'a', text: 'The offer required written acceptance and the reply was oral' },
        { id: 'b', text: 'There was no consideration' },
        { id: 'c', text: 'One party lacked capacity' },
      ],
      correctOptionId: 'a',
      explanation:
        'Paragraph 12 of the fictitious judgment turns on the written-acceptance requirement; paragraph 15 says consideration was not decided.',
      citationIds: ['psg_demo_ab_p12'],
    },
  ],
};

export const DEMO_EDITORIAL_TASKS: EditorialTask[] = [
  {
    id: 'task_demo_1',
    dataOrigin: 'demo',
    sourceId: 'src_demo_alpha_v_beta',
    title: 'Check extracted text against original',
    dimension: 'text_integrity',
    status: 'under_review',
    assigneeLabel: 'Demo Legal Editor',
    priority: 'high',
    updatedAt: '2026-10-18',
  },
  {
    id: 'task_demo_2',
    dataOrigin: 'demo',
    sourceId: 'src_demo_alpha_v_beta',
    title: 'Assess legal status and later treatment',
    dimension: 'legal_status',
    status: 'needs_more_evidence',
    assigneeLabel: 'Demo Legal Reviewer',
    priority: 'normal',
    updatedAt: '2026-10-17',
  },
  {
    id: 'task_demo_3',
    dataOrigin: 'demo',
    sourceId: 'src_demo_tenancy_rules',
    title: 'Confirm provenance and publication rights',
    dimension: 'authenticity',
    status: 'review_ready',
    assigneeLabel: 'Unassigned',
    priority: 'normal',
    updatedAt: '2026-10-15',
  },
  {
    id: 'task_demo_4',
    dataOrigin: 'demo',
    sourceId: 'src_demo_contracts_act',
    title: 'Status review completed',
    dimension: 'legal_status',
    status: 'approved_for_defined_use',
    assigneeLabel: 'Demo Legal Reviewer',
    priority: 'low',
    updatedAt: '2026-09-30',
  },
];
